import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { interpolate } from 'remotion';
import {timestampToSeconds, timestampToSecondsMs} from '../../helpers/AppHelper.jsx';
import _ from 'lodash';

// Helper function to format element size
const useElementSize = (ref) => {
    const [size, setSize] = useState(() => {
        if (!ref.current) {
            return null;
        }

        const rect = ref.current.getClientRects();
        if (!rect[0]) {
            return null;
        }

        return {
            width: rect[0].width,
            height: rect[0].height,
            left: rect[0].x,
            top: rect[0].y,
        };
    });

    const observer = useMemo(() => {
        if (typeof ResizeObserver === 'undefined') {
            return null;
        }

        return new ResizeObserver((entries) => {
            const { target } = entries[0];
            const newSize = target.getClientRects();

            if (!newSize?.[0]) {
                setSize(null);
                return;
            }

            setSize({
                width: newSize[0].width,
                height: newSize[0].height,
                left: newSize[0].x,
                top: newSize[0].y,
            });
        });
    }, []);

    const updateSize = useCallback(() => {
        if (!ref.current) {
            return;
        }

        const rect = ref.current.getClientRects();
        if (!rect[0]) {
            setSize(null);
            return;
        }

        setSize((prevState) => {
            const isSame =
              prevState &&
              prevState.width === rect[0].width &&
              prevState.height === rect[0].height &&
              prevState.left === rect[0].x &&
              prevState.top === rect[0].y;
            if (isSame) {
                return prevState;
            }

            return {
                width: rect[0].width,
                height: rect[0].height,
                left: rect[0].x,
                top: rect[0].y,
                windowSize: {
                    height: window.innerHeight,
                    width: window.innerWidth,
                },
            };
        });
    }, [ref]);

    useEffect(() => {
        if (!observer) {
            return;
        }

        const { current } = ref;
        if (current) {
            observer.observe(current);
        }

        return () => {
            if (current) {
                observer.unobserve(current);
            }
        };
    }, [observer, ref, updateSize]);

    useEffect(() => {
        window.addEventListener('resize', updateSize);

        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, [updateSize]);

    return useMemo(() => {
        if (!size) {
            return null;
        }

        return { ...size, refresh: updateSize };
    }, [size, updateSize]);
};

// Helper function to get frame from X position
const getFrameFromX = (clientX, durationInFrames, width) => {
    const pos = clientX;
    const frame = Math.round(
      interpolate(pos, [0, width], [0, Math.max(durationInFrames - 1, 0)], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
      })
    );
    return frame;
};

const BAR_HEIGHT = 5;
const KNOB_SIZE = 12;
const VERTICAL_PADDING = 4;

// Helper function to find body in which div is located
const findBodyInWhichDivIsLocated = (div) => {
    let current = div;

    while (current.parentElement) {
        current = current.parentElement;
    }

    return current;
};

const useHoverState = (ref) => {
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        const { current } = ref;
        if (!current) {
            return;
        }

        const onHover = () => {
            setHovered(true);
        };

        const onLeave = () => {
            setHovered(false);
        };

        const onMove = () => {
            setHovered(true);
        };

        current.addEventListener('mouseenter', onHover);
        current.addEventListener('mouseleave', onLeave);
        current.addEventListener('mousemove', onMove);

        return () => {
            current.removeEventListener('mouseenter', onHover);
            current.removeEventListener('mouseleave', onLeave);
            current.removeEventListener('mousemove', onMove);
        };
    }, [ref]);
    return hovered;
};

// SeekBar component
export default function SeekBar({ durationInFrames, scrollToPosition, fps, segments, playerRef, inFrame = 0, outFrame = 0 }) {
    const containerRef = useRef(null);
    const barHovered = useHoverState(containerRef);
    const size = useElementSize(containerRef);
    const [playing, setPlaying] = useState(false);
    const [frame, setFrame] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);

    const autoScrollToSegment = (newTime) => {
        const currentSegmentIndex = segments.findIndex((segment) => {
            const startTime = timestampToSecondsMs(segment?.timestamp?.start);
            const endTime = timestampToSecondsMs(segment?.timestamp?.end);
            return newTime >= startTime && newTime <= endTime;
        });

        if (currentSegmentIndex == -1) {
            if (typeof scrollToPosition === "function") {
                scrollToPosition(_.lastIndexOf(segments))
            }
        } else {
            if (typeof scrollToPosition === "function") {
                scrollToPosition(currentSegmentIndex)
            }
        }
    };

    useEffect(() => {
        const { current } = playerRef;
        if (!current) {
            return;
        }

        const onFrameUpdate = () => {
            if (!isSeeking) {
                setFrame(current.getCurrentFrame());
            }
        };

        current.addEventListener('frameupdate', onFrameUpdate);

        return () => {
            current.removeEventListener('frameupdate', onFrameUpdate);
        };
    }, [playerRef, isSeeking]);

    useEffect(() => {
        const { current } = playerRef;
        if (!current) {
            return;
        }

        const onPlay = () => {
            setPlaying(true);
        };

        const onPause = () => {
            setPlaying(false);
        };

        const onSeeked = () => {
            setIsSeeking(false);
        };

        current.addEventListener('play', onPlay);
        current.addEventListener('pause', onPause);
        current.addEventListener('seeked', onSeeked);

        return () => {
            current.removeEventListener('play', onPlay);
            current.removeEventListener('pause', onPause);
            current.removeEventListener('seeked', onSeeked);
        };
    }, [playerRef]);

    const [dragging, setDragging] = useState({
        dragging: false,
    });

    const width = size?.width ?? 0;

    const onPointerDown = useCallback(
      (e) => {
          if (e.button !== 0) {
              return;
          }

          if (!playerRef.current) {
              return;
          }

          const posLeft = containerRef.current?.getBoundingClientRect().left;

          const _frame = getFrameFromX(
            e.clientX - posLeft,
            durationInFrames,
            width
          );

          const wasPlaying = playing;

          // Immediately pause and set seeking state
          setIsSeeking(true);
          if (wasPlaying) {
              playerRef.current.pause();
          }

          // Update frame immediately for visual feedback
          setFrame(_frame);

          // Perform the actual seek
          playerRef.current.seekTo(_frame);
          autoScrollToSegment(_frame/fps);

          setDragging({
              dragging: true,
              wasPlaying: wasPlaying,
          });
      },
      [durationInFrames, width, playerRef, playing, fps]
    );

    const onPointerMove = useCallback(
      (e) => {
          if (!size) {
              return;
          }

          if (!dragging.dragging) {
              return;
          }

          if (!playerRef.current) {
              return;
          }

          const posLeft = containerRef.current?.getBoundingClientRect().left;

          const _frame = getFrameFromX(
            e.clientX - posLeft,
            durationInFrames,
            size.width
          );

          // Update frame immediately for visual feedback
          setFrame(_frame);

          // Perform seek
          playerRef.current.seekTo(_frame);
      },
      [dragging.dragging, durationInFrames, playerRef, size]
    );

    const onPointerUp = useCallback(() => {
        if (!dragging.dragging) {
            return;
        }

        if (!playerRef.current) {
            return;
        }

        const wasPlaying = dragging.wasPlaying;

        setDragging({
            dragging: false,
        });

        // Wait longer for video to buffer at the new position
        if (wasPlaying) {
            setTimeout(() => {
                setIsSeeking(false);
                if (playerRef.current) {
                    playerRef.current.play();
                }
            }, 200);
        } else {
            setTimeout(() => {
                setIsSeeking(false);
            }, 100);
        }
    }, [dragging, playerRef]);

    useEffect(() => {
        if (!dragging.dragging) {
            return;
        }

        const body = findBodyInWhichDivIsLocated(containerRef.current);

        body.addEventListener('pointermove', onPointerMove);
        body.addEventListener('pointerup', onPointerUp);
        return () => {
            body.removeEventListener('pointermove', onPointerMove);
            body.removeEventListener('pointerup', onPointerUp);
        };
    }, [dragging.dragging, onPointerMove, onPointerUp]);

    const knobStyle = useMemo(() => {
        return {
            left: Math.max(
              0,
              (frame / Math.max(1, durationInFrames - 1)) * width - KNOB_SIZE / 2
            ),
            transition: isSeeking ? 'none' : 'opacity 0.1s ease',
        };
    }, [durationInFrames, frame, width, isSeeking]);

    const fillStyle = useMemo(() => {
        return {
            width: ((frame - (inFrame ?? 0)) / (durationInFrames - 1)) * 100 + '%',
            marginLeft: ((inFrame ?? 0) / (durationInFrames - 1)) * 100 + '%',
        };
    }, [durationInFrames, frame, inFrame]);

    const active = useMemo(() => {
        return {
            width:
              (((outFrame ?? durationInFrames - 1) - (inFrame ?? 0)) /
                (durationInFrames - 1)) *
              100 + '%',
            marginLeft: ((inFrame ?? 0) / (durationInFrames - 1)) * 100 + '%',
        };
    }, [durationInFrames, inFrame, outFrame]);

    return (
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        className="flex cursor-pointer touch-none box-border select-none relative flex-shrink-0">
          <div className="h-2.5 bg-secondary-200 w-full">
              <div style={active} className="h-1.5 opacity-50 bg-black rounded-2xl absolute mt-0.5"/>
              <div style={fillStyle} className="bg-secondary-500 h-2.5 rounded-r-3xl z-[10]"/>
          </div>
          <div style={knobStyle} className="absolute w-3.5 aspect-square rounded-full bg-secondary-500 shadow -mt-0.5 -ml-0.5"/>
      </div>
    );
}